import React from "react";
import {
  Box,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  IconButton,
} from "@material-ui/core";
import messages from "./messages.json";
import { init, reducer } from "./state";
import { AccessList } from "./access-list";
import { Access } from "../../../../../context";
import { AccessForm, Handlers } from "./access-form";
import { Actions, Robot } from "../../../../../components";
import { useRequestContext, System } from "../../../../../context";
import CloseIcon from "@material-ui/icons/Close";

const MESSAGES = (messages as unknown) as Record<string, string>;

type Props = {
  system: System | null;
  onClose: () => void;
  open: boolean;
};

export const AddSystemModal: React.FC<Props> = ({ onClose, open, system }) => {
  const [, requestContextDispatch] = useRequestContext();
  const form = React.useRef<Handlers>(null);
  const [localState, localDispatch] = React.useReducer(
    reducer,
    system?.accesses,
    init
  );

  React.useEffect(() => {
    if (open) {
      localDispatch({
        type: "reset",
        accesses: system?.accesses,
      });
      form.current?.reset();
    }
  }, [open, system]);

  const checkSystemRemoval = React.useCallback(() => {
    if (!!system && localState.accesses.length === 0) {
      requestContextDispatch({
        type: "remove-system",
        system: system,
      });
    }
  }, [localState.accesses.length, requestContextDispatch, system]);

  return system ? (
    <Dialog
      fullWidth
      maxWidth="lg"
      onClose={() => {
        checkSystemRemoval();
        onClose();
      }}
      open={open}
      scroll="paper"
    >
      <DialogTitle disableTypography>
        <Grid container spacing={2}>
          <Grid item xs={3} />
          <Grid
            item
            container
            direction="row"
            xs={9}
            justify="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle2">
              {system.application.name} | {system.application.host}
            </Typography>
            <IconButton
              onClick={() => {
                checkSystemRemoval();
                onClose();
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Robot
              message={localState.message.content}
              error={localState.message.error}
            />
          </Grid>
          <Grid container item xs={3} spacing={2}>
            <Grid item xs={12}>
              <AccessForm
                application={system.application}
                onChange={(key, value: any) => {
                  if (!system.application.manual) {
                    switch (key) {
                      case "environment":
                        localDispatch({
                          type: "set-message",
                          message: {
                            content: MESSAGES.environment_selected.replace(
                              "[[ENVIRONMENT]]",
                              value?.name || value
                            ),
                          },
                        });
                        break;
                      case "role":
                        localDispatch({
                          type: "set-message",
                          message: {
                            content: MESSAGES.role_selected.replace(
                              "[[ROLE]]",
                              value?.name || value
                            ),
                          },
                        });
                        break;
                      default:
                        break;
                    }
                  }
                }}
                onError={() => {
                  localDispatch({
                    type: "set-message",
                    message: {
                      content: MESSAGES.missing_information_error,
                      error: true,
                    },
                  });
                }}
                onSubmit={(access: Access) => {
                  localDispatch({
                    type: "add-access",
                    access,
                  });
                }}
                ref={form}
              />
            </Grid>
            <Grid item xs={12}>
              <Actions>
                <Actions.Action
                  outlined
                  onClick={() => {
                    form.current?.submit();
                  }}
                >
                  + Add to list
                </Actions.Action>
              </Actions>
            </Grid>
          </Grid>
          <Grid
            item
            xs={6}
            component={Box}
            display="flex"
            flexDirection="column"
          >
            <AccessList
              accesses={localState.accesses}
              application={system.application}
              onRemove={(access) => {
                localDispatch({
                  type: "remove-access",
                  access,
                });
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Actions>
              <Actions.Action
                outlined
                onClick={() => {
                  checkSystemRemoval();
                  onClose();
                }}
              >
                Cancel
              </Actions.Action>
              <Actions.Action
                disabled={!localState.accesses.length}
                onClick={() => {
                  if (localState.create) {
                    requestContextDispatch({
                      type: "add-system",
                      system: {
                        ...system,
                        accesses: localState.accesses,
                      },
                    });
                  } else {
                    requestContextDispatch({
                      type: "set-system-accesses",
                      system,
                      accesses: localState.accesses,
                    });
                  }
                  checkSystemRemoval();
                  onClose();
                }}
              >
                Submit
              </Actions.Action>
            </Actions>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  ) : null;
};
